import axios, { isAxiosError } from "axios";
import type { AxiosResponse } from "axios";

import { getApiErrorMessage, headers, host } from "./shared";
import type {
  OwnershipTransfer,
  School,
  SchoolAttribution,
  SchoolInput,
  SchoolMember,
  SchoolStudent,
  SchoolTeacher,
} from "../../types/school";

type AccessToken = string | null | undefined;

type ErrorWithResponse = Error & { response?: AxiosResponse };

// Bounds ownership transfer requests so a hung request can't trap a caller
// (e.g. the review modal, which has no dismiss option while loading) waiting
// indefinitely.
export const REQUEST_TIMEOUT_MS: number = 15000;

// Re-throws the server's own error message where there is one (keeping the
// original response attached), or the original error unchanged otherwise.
const wrapApiError = (error: unknown): never => {
  const errorMessage = getApiErrorMessage(error);
  if (!errorMessage || !isAxiosError(error)) {
    throw error;
  }
  const wrappedError: ErrorWithResponse = new Error(errorMessage);
  wrappedError.response = error.response;
  throw wrappedError;
};

export const createSchool = async (
  school: SchoolInput,
  accessToken: AccessToken,
  attributionData: SchoolAttribution = {},
): Promise<AxiosResponse<School>> => {
  return await axios.post(
    `${host}/api/schools`,
    { school, ...attributionData },
    headers(accessToken),
  );
};

export const getSchool = async (
  schoolId: string,
  accessToken: AccessToken,
): Promise<School> => {
  const response = await axios.get(
    `${host}/api/schools/${schoolId}`,
    headers(accessToken),
  );
  return response.data;
};

export const updateSchool = async (
  schoolId: string,
  schoolUpdates: SchoolInput,
  accessToken: AccessToken,
): Promise<AxiosResponse<School>> => {
  try {
    return await axios.put(
      `${host}/api/schools/${schoolId}`,
      { school: schoolUpdates },
      headers(accessToken),
    );
  } catch (error) {
    const errorMessage = getApiErrorMessage(error);
    throw errorMessage ? new Error(errorMessage) : error;
  }
};

export const getUserSchool = async (
  accessToken: AccessToken,
): Promise<School | undefined> => {
  let response: AxiosResponse<School>;
  try {
    response = await axios.get(`${host}/api/school`, headers(accessToken));
    return response.data;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return;
    } else {
      throw error;
    }
  }
};

export const getOwnershipTransfer = async (
  schoolId: string,
  accessToken: AccessToken,
  signal?: AbortSignal,
): Promise<OwnershipTransfer | undefined> => {
  try {
    const response = await axios.get(
      `${host}/api/schools/${schoolId}/ownership_transfer`,
      { ...headers(accessToken), timeout: REQUEST_TIMEOUT_MS, signal },
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return;
    } else {
      throw error;
    }
  }
};

export const createOwnershipTransfer = async (
  schoolId: string,
  nominatedUserId: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<void>> => {
  try {
    return await axios.post(
      `${host}/api/schools/${schoolId}/ownership_transfer`,
      { ownership_transfer: { nominated_user_id: nominatedUserId } },
      headers(accessToken),
    );
  } catch (error) {
    wrapApiError(error);
  }
};

export const acceptOwnershipTransfer = async (
  schoolId: string,
  accessToken: AccessToken,
  signal?: AbortSignal,
): Promise<AxiosResponse<void>> => {
  try {
    return await axios.put(
      `${host}/api/schools/${schoolId}/ownership_transfer/accept`,
      {},
      { ...headers(accessToken), timeout: REQUEST_TIMEOUT_MS, signal },
    );
  } catch (error) {
    wrapApiError(error);
  }
};

export const declineOwnershipTransfer = async (
  schoolId: string,
  accessToken: AccessToken,
  signal?: AbortSignal,
): Promise<AxiosResponse<void>> => {
  try {
    return await axios.put(
      `${host}/api/schools/${schoolId}/ownership_transfer/decline`,
      {},
      { ...headers(accessToken), timeout: REQUEST_TIMEOUT_MS, signal },
    );
  } catch (error) {
    wrapApiError(error);
  }
};

export const cancelOwnershipTransfer = async (
  schoolId: string,
  accessToken: AccessToken,
  signal?: AbortSignal,
): Promise<AxiosResponse<void>> => {
  try {
    return await axios.put(
      `${host}/api/schools/${schoolId}/ownership_transfer/cancel`,
      {},
      { ...headers(accessToken), timeout: REQUEST_TIMEOUT_MS, signal },
    );
  } catch (error) {
    wrapApiError(error);
  }
};

export const getSchoolMembers = async (
  schoolId: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<SchoolMember[]>> => {
  return await axios.get(
    `${host}/api/schools/${schoolId}/members`,
    headers(accessToken),
  );
};

export const getSchoolTeachers = async (
  schoolId: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<SchoolTeacher[]>> => {
  return await axios.get(
    `${host}/api/schools/${schoolId}/teachers`,
    headers(accessToken),
  );
};

export const getSchoolStudents = async (
  schoolId: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<SchoolStudent[]>> => {
  return await axios.get(
    `${host}/api/schools/${schoolId}/students`,
    headers(accessToken),
  );
};

export const inviteTeacherToSchool = async (
  schoolId: string,
  teacherEmail: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<void>> => {
  return await axios.post(
    `${host}/api/schools/${schoolId}/teachers`,
    { email_address: teacherEmail },
    headers(accessToken),
  );
};

export const createSchoolEmailDomain = async (
  schoolId: string,
  schoolEmailDomain: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<{ domain: string }>> => {
  return await axios.post(
    `${host}/api/schools/${schoolId}/school_email_domains`,
    {
      school_email_domain: {
        domain: schoolEmailDomain,
      },
    },
    headers(accessToken),
  );
};

export const getSchoolEmailDomains = async (
  schoolId: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<string[]>> => {
  return await axios.get(
    `${host}/api/schools/${schoolId}/school_email_domains`,
    headers(accessToken),
  );
};
